/**
 * Quest View
 * Active quest gameplay interface
 */

import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonSpinner,
  IonBackButton,
  IonButtons,
  IonIcon,
  IonText,
  IonChip,
  IonProgressBar,
  useIonAlert,
} from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import { timeOutline, peopleOutline, heartOutline, shieldOutline, mapOutline } from 'ionicons/icons';
import {
  getGuildQuest,
  getQuestNode,
  getNodeChoices,
  makeChoice,
  getQuestProgress,
  performCombatAction,
  getQuestEndings,
} from '../../../services/pipsGuildQuestService';
import {
  GuildQuest,
  QuestNode,
  QuestChoice,
  QuestProgress,
  QuestEnding,
  PlayerStats,
  CombatActionType,
} from '../../../types/pipsGuildQuest';
import { supabase } from '../../../services/supabase';
import CombatView from './CombatView';
import QuestComplete from './QuestComplete';
import './QuestView.css';
import { logger } from '../../../utils/logger';

interface QuestViewParams {
  questId: string;
}

const QuestView: React.FC = () => {
  const { questId } = useParams<QuestViewParams>();
  const history = useHistory();
  const [presentAlert] = useIonAlert();

  const [loading, setLoading] = useState(true);
  const [quest, setQuest] = useState<GuildQuest | null>(null);
  const [currentNode, setCurrentNode] = useState<QuestNode | null>(null);
  const [choices, setChoices] = useState<QuestChoice[]>([]);
  const [progress, setProgress] = useState<QuestProgress[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [showWinScreen, setShowWinScreen] = useState(false);
  const [questEnding, setQuestEnding] = useState<QuestEnding | null>(null);

  useEffect(() => {
    const init = async () => {
      await getCurrentUser();
      await loadQuestData();
    };

    init();

    // Set up real-time subscription
    const subscription = supabase
      .channel(`quest:${questId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'guild_quests',
          filter: `id=eq.${questId}`,
        },
        () => {
          loadQuestData();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [questId]);

  // Re-check turn status when currentUserId changes
  useEffect(() => {
    if (quest && currentUserId) {
      const currentTurnUserId = (quest as any).current_turn_user_id || quest.currentTurnUserId;
      logger.log('🎲 Turn check - Current user:', currentUserId, 'Turn user:', currentTurnUserId);
      setIsMyTurn(currentTurnUserId === currentUserId);
    }
  }, [currentUserId, quest]);

  useEffect(() => {
    if (!quest) return;

    // Update time remaining
    const updateTimer = () => {
      const turnStartedAt = (quest as any).turn_started_at || quest.turnStartedAt;
      if (turnStartedAt) {
        const turnStart = new Date(turnStartedAt).getTime();
        const now = Date.now();
        const elapsed = now - turnStart;
        const remaining = 12 * 60 * 60 * 1000 - elapsed; // 12 hours

        if (remaining <= 0) {
          setTimeRemaining('Time expired');
        } else {
          const hours = Math.floor(remaining / (60 * 60 * 1000));
          const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
          setTimeRemaining(`${hours}h ${minutes}m`);

          // Show warning when under 2 hours
          if (remaining < 2 * 60 * 60 * 1000 && remaining >= 1 * 60 * 60 * 1000 && isMyTurn) {
            logger.warn('⏰ Less than 2 hours remaining on turn!');
          }
        }
      }
    };

    // Initial update
    updateTimer();

    // Update every minute
    const interval = setInterval(updateTimer, 60000);

    return () => clearInterval(interval);
  }, [quest, isMyTurn]);

  const getCurrentUser = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (user) {
      setCurrentUserId(user.id);
    }
  };

  // Get badge color based on remaining time
  const getTimerBadgeColor = (): string => {
    if (!quest || !timeRemaining || timeRemaining === 'Time expired') {
      return 'danger';
    }

    const turnStartedAt = (quest as any).turn_started_at || quest.turnStartedAt;
    if (!turnStartedAt) return 'success';

    const turnStart = new Date(turnStartedAt).getTime();
    const now = Date.now();
    const remaining = 12 * 60 * 60 * 1000 - (now - turnStart);

    // Less than 30 minutes: danger (red)
    if (remaining < 30 * 60 * 1000) {
      return 'danger';
    }
    // Less than 2 hours: warning (yellow)
    if (remaining < 2 * 60 * 60 * 1000) {
      return 'warning';
    }
    // 2+ hours: success (green)
    return 'success';
  };

  const loadQuestData = async () => {
    try {
      const questData = await getGuildQuest(questId);
      logger.log('📊 Quest data loaded:', questData);

      if (!questData) {
        presentAlert({
          header: 'Error',
          message: 'Quest not found',
          buttons: ['OK'],
        });
        history.goBack();
        return;
      }

      setQuest(questData);
      // Database returns snake_case field names
      const currentTurnUserId = (questData as any).current_turn_user_id || questData.currentTurnUserId;
      setIsMyTurn(currentTurnUserId === currentUserId);

      const currentNodeId = (questData as any).current_node_id || questData.currentNodeId;
      const nodeData = await getQuestNode(currentNodeId);
      logger.log('📖 Node data loaded:', nodeData);
      setCurrentNode(nodeData);

      if (nodeData) {
        const nodeType = (nodeData as any).node_type || nodeData.nodeType;
        if (nodeType === 'story') {
          const choicesData = await getNodeChoices(nodeData.id);
          logger.log('🎯 Choices loaded:', choicesData);
          setChoices(choicesData);
        }
      }

      const progressData = await getQuestProgress(questId);
      setProgress(progressData);
    } catch (error) {
      logger.error('Error loading quest data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMakeChoice = async (choiceId: string) => {
    if (!isMyTurn) {
      presentAlert({
        header: 'Not Your Turn',
        message: 'Please wait for your turn to make a choice.',
        buttons: ['OK'],
      });
      return;
    }

    setLoading(true);
    const result = await makeChoice(questId, currentUserId, choiceId);

    if (result.success) {
      // Don't show popup for quest completion - let the ending node display naturally
      if (result.combatTriggered) {
        presentAlert({
          header: 'Combat!',
          message: 'You have entered combat!',
          buttons: ['OK'],
        });
      }
      await loadQuestData();
    } else {
      presentAlert({
        header: 'Error',
        message: result.error || 'Failed to make choice',
        buttons: ['OK'],
      });
      setLoading(false);
    }
  };

  const getPartyStats = () => {
    if (!quest) {
      logger.log('⚠️ No quest data');
      return [];
    }
    const partyStats = (quest as any).party_stats || quest.partyStats;
    logger.log('👥 Party stats:', partyStats, 'Type:', typeof partyStats);

    // Null check: typeof null === 'object' in JavaScript, so we need explicit null check
    if (!partyStats || partyStats === null || typeof partyStats !== 'object' || Array.isArray(partyStats)) {
      logger.log('⚠️ Invalid party stats, returning empty array');
      return [];
    }

    try {
      const stats = partyStats as Record<string, PlayerStats>;
      const entries = Object.entries(stats).map(([userId, stat]) => ({
        userId,
        ...stat,
      }));
      logger.log('✅ Party stats entries:', entries);
      return entries;
    } catch (error) {
      logger.error('❌ Error parsing party stats:', error);
      return [];
    }
  };

  const getQuestProgressInfo = () => {
    const nodesVisited = progress.length;
    // Estimate 15-20 nodes for average quest length
    const estimatedTotal = 18;
    const progressPercent = Math.min(nodesVisited / estimatedTotal, 1);

    // Determine quest phase
    let phase = 'Beginning';
    if (nodesVisited >= 15) phase = 'Near End';
    else if (nodesVisited >= 8) phase = 'Mid Quest';
    else if (nodesVisited >= 4) phase = 'Early Journey';

    return {
      nodesVisited,
      estimatedTotal,
      progressPercent,
      phase,
    };
  };

  const handleCombatAction = async (
    actionType: CombatActionType,
    targetId?: string,
    abilityId?: string
  ) => {
    if (!quest) return;

    const result = await performCombatAction(questId, currentUserId, {
      userId: currentUserId,
      actionType,
      targetId,
      abilityId,
    });

    if (result.success) {
      if (result.combatEnded) {
        if (result.victory) {
          presentAlert({
            header: 'Victory!',
            message: 'You have defeated the enemies!',
            buttons: ['Continue'],
          });
        } else {
          presentAlert({
            header: 'Defeated',
            message: 'Your party has been defeated...',
            buttons: ['OK'],
          });
        }
      }
      await loadQuestData();
    } else {
      presentAlert({
        header: 'Error',
        message: result.error || 'Combat action failed',
        buttons: ['OK'],
      });
    }
  };

  const handleViewWinScreen = async () => {
    if (!quest) return;

    // Fetch the ending data
    const questTemplateId = (quest as any).quest_template_id || quest.questTemplateId;
    const endings = await getQuestEndings(questTemplateId);

    let ending: QuestEnding | undefined;

    // Try to get the specific ending if endingId is set
    const endingId = (quest as any).ending_id || quest.endingId;
    if (endingId) {
      ending = endings.find(e => e.id === endingId);
    }

    // Fallback: use the first available ending
    if (!ending && endings.length > 0) {
      ending = endings[0];
    }

    if (ending) {
      setQuestEnding(ending);
      setShowWinScreen(true);
    }
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/games/pips-guild-quest" />
            </IonButtons>
            <IonTitle>Quest</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="loading-container">
            <IonSpinner />
            <p>Loading quest...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!quest || !currentNode) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/games/pips-guild-quest" />
            </IonButtons>
            <IonTitle>Quest</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <p>Quest not found</p>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/games/pips-guild-quest" />
          </IonButtons>
          <IonTitle>Quest</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="quest-view">
        {/* Turn Status */}
        <div className="turn-status">
          {isMyTurn ? (
            <IonBadge color={getTimerBadgeColor()} className="turn-badge">
              <IonIcon icon={timeOutline} />
              Your Turn - {timeRemaining || 'Calculating...'}
            </IonBadge>
          ) : (
            <IonBadge color="medium" className="turn-badge">
              <IonIcon icon={timeOutline} />
              Waiting for other player - {timeRemaining || 'Calculating...'}
            </IonBadge>
          )}
        </div>

        {/* Quest Progress Indicator */}
        <IonCard className="progress-card">
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={mapOutline} /> Quest Progress
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <div className="progress-info">
              <div className="progress-stats">
                <IonBadge color="primary">
                  {getQuestProgressInfo().nodesVisited} Nodes Visited
                </IonBadge>
                <IonBadge color="secondary">
                  {getQuestProgressInfo().phase}
                </IonBadge>
              </div>
              <IonProgressBar
                value={getQuestProgressInfo().progressPercent}
                color="primary"
                style={{ marginTop: '12px', height: '8px', borderRadius: '4px' }}
              />
              <IonText color="medium" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                Estimated progress based on typical quest length
              </IonText>
            </div>
          </IonCardContent>
        </IonCard>

        {/* Party Status */}
        <IonCard className="party-card">
          <IonCardHeader>
            <IonCardTitle>
              <IonIcon icon={peopleOutline} /> Party
            </IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <div className="party-grid">
              {getPartyStats().map((member) => {
                const currentTurnUserId = (quest as any).current_turn_user_id || quest.currentTurnUserId;
                return (
                  <div key={member.userId} className="party-member">
                    <div className="member-name">
                      Player
                      {currentTurnUserId === member.userId && (
                        <IonChip color="primary" className="turn-chip">
                          Current Turn
                        </IonChip>
                      )}
                    </div>
                    <div className="member-stats">
                      <span>
                        <IonIcon icon={heartOutline} /> {member.hp}/{member.maxHp}
                      </span>
                      <span>
                        <IonIcon icon={shieldOutline} /> DEF {member.def}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </IonCardContent>
        </IonCard>

        {/* Current Node */}
        <IonCard className="story-card">
          <IonCardHeader>
            <IonCardTitle>{currentNode.title}</IonCardTitle>
            {(() => {
              const nodeType = (currentNode as any).node_type || currentNode.nodeType;
              return nodeType !== 'story' && <IonBadge color="warning">{nodeType}</IonBadge>;
            })()}
          </IonCardHeader>
          <IonCardContent>
            <div className="story-text">{(currentNode as any).story_text || currentNode.storyText}</div>
          </IonCardContent>
        </IonCard>

        {/* Quest Inventory */}
        {quest.inventory && (quest.inventory as any[]).length > 0 && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Inventory</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="inventory-items">
                {(quest.inventory as any[]).map((item, index) => (
                  <IonChip key={index}>
                    {item.name} {item.quantity > 1 && `(${item.quantity})`}
                  </IonChip>
                ))}
              </div>
            </IonCardContent>
          </IonCard>
        )}

        {/* Choices */}
        {(() => {
          const nodeType = (currentNode as any).node_type || currentNode.nodeType;
          return nodeType === 'story' && choices.length > 0 && (
            <IonCard className="choices-card">
              <IonCardHeader>
                <IonCardTitle>Your Choices</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList>
                  {choices.map((choice) => {
                    const choiceText = (choice as any).choice_text || choice.choiceText;
                    const flavorText = (choice as any).flavor_text || choice.flavorText;
                    const classRequirement = (choice as any).class_requirement || choice.classRequirement;
                    const itemRequirement = (choice as any).item_requirement || choice.itemRequirement;
                    return (
                      <IonItem
                        key={choice.id}
                        button
                        onClick={() => handleMakeChoice(choice.id)}
                        disabled={!isMyTurn}
                        className="choice-item"
                      >
                        <IonLabel className="ion-text-wrap">
                          <h3>{choiceText}</h3>
                          {flavorText && <p>{flavorText}</p>}
                          {classRequirement && (
                            <p className="requirement">
                              <IonBadge color="primary">{classRequirement} only</IonBadge>
                            </p>
                          )}
                          {itemRequirement && (
                            <p className="requirement">
                              <IonBadge color="warning">Requires: {itemRequirement}</IonBadge>
                            </p>
                          )}
                        </IonLabel>
                      </IonItem>
                    );
                  })}
                </IonList>
              </IonCardContent>
            </IonCard>
          );
        })()}

        {/* Combat */}
        {(() => {
          const nodeType = (currentNode as any).node_type || currentNode.nodeType;
          const combatState = (quest as any).combat_state || quest.combatState;
          const partyStats = (quest as any).party_stats || quest.partyStats || {};
          return nodeType === 'combat' && combatState && (
            <CombatView
              combat={combatState}
              playerStats={partyStats as Record<string, PlayerStats>}
              currentUserId={currentUserId}
              isMyTurn={isMyTurn && combatState.active}
              onPerformAction={handleCombatAction}
            />
          );
        })()}

        {/* Ending */}
        {(() => {
          const nodeType = (currentNode as any).node_type || currentNode.nodeType;
          if (nodeType !== 'ending') return null;

          // Show win screen if player has clicked Continue
          if (showWinScreen && questEnding) {
            return (
              <QuestComplete
                ending={questEnding}
                goldEarned={questEnding.goldReward}
                xpEarned={questEnding.xpReward}
                onReturnToHub={() => history.push('/games/pips-guild-quest')}
              />
            );
          }

          // Otherwise show Continue button after the story
          return (
            <IonCard className="ending-card">
              <IonCardContent>
                <IonButton
                  expand="block"
                  size="large"
                  onClick={handleViewWinScreen}
                  className="ion-margin-top"
                >
                  Continue
                </IonButton>
              </IonCardContent>
            </IonCard>
          );
        })()}

        {/* Quest Log */}
        {progress.length > 0 && (
          <IonCard className="quest-log-card">
            <IonCardHeader>
              <IonCardTitle>Quest Log</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonList>
                {progress.slice(-5).map((entry, index) => {
                  const outcomeText = (entry as any).outcome_text || entry.outcomeText;
                  const createdAt = (entry as any).created_at || entry.createdAt;
                  return (
                    <IonItem key={entry.id} lines={index < 4 ? 'inset' : 'none'}>
                      <IonLabel className="ion-text-wrap">
                        <p className="log-entry">{outcomeText}</p>
                        <p className="log-timestamp">
                          {new Date(createdAt).toLocaleString()}
                        </p>
                      </IonLabel>
                    </IonItem>
                  );
                })}
              </IonList>
            </IonCardContent>
          </IonCard>
        )}
      </IonContent>
    </IonPage>
  );
};

export default QuestView;
